// BEGIN CUSTOM EDIT: Continuous collection navigation | Date: 2026-09-15
// Shopify pages remain internal fetch units; browser history stores continuity without page URLs.
class CollectionInfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.observer = null;
    this.isLoading = false;
    this.hasError = false;
    this.isReplacing = false;
    this.autoLoadArmed = false;
    // BEGIN CUSTOM EDIT: Collection completion announcements | Date: 2026-09-15 | What: Do not announce completion on an already-final direct entry
    this.hasLoadedPage = false;
    // END CUSTOM EDIT
    this.loadedKeys = new Set();
    this.isRestoring = false;
    this.restorationId = 0;
    this.pendingRestore = null;
    this.onLoadMoreClick = this.onLoadMoreClick.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onFacetUpdate = this.onFacetUpdate.bind(this);
    this.onFacetUpdateComplete = this.onFacetUpdateComplete.bind(this);
    this.onProductLinkClick = this.onProductLinkClick.bind(this);
    this.onPageHide = this.onPageHide.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onRestoreIntent = this.onRestoreIntent.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    // BEGIN CUSTOM EDIT: Collection section navigation lifecycle | Date: 2026-09-16 | What: Suspend infinite loading while another collection is section-rendered
    this.onCollectionNavigationStart = this.onCollectionNavigationStart.bind(this);
    this.onCollectionNavigationComplete = this.onCollectionNavigationComplete.bind(this);
    // END CUSTOM EDIT
  }

  connectedCallback() {
    this.section = this.closest(".shopify-section");
    this.sectionId = this.getAttribute("section-id") || this.section?.id?.replace("shopify-section-", "");
    this.currentPage = Number(this.dataset.page || 1);
    this.pageCount = Number(this.dataset.pageCount || 1);
    this.productList = this.querySelector("[data-collection-product-list]");
    this.pagination = this.querySelector("[data-collection-pagination]");
    this.statusElement = this.querySelector("[data-load-status]");
    this.setExistingItemPages(this.currentPage);
    this.firstLoadedPage = Math.min(this.currentPage, ...Array.from(this.productList?.children || [], item => Number(item.dataset.page) || this.currentPage));
    this.loadedKeys = new Set(Array.from(this.productList?.children || []).map((item) => this.itemKey(item)).filter(Boolean));
    this.collectionUrl = this.cleanUrl(new URL(window.location.href));
    this.requestContext = this.contextKey(this.collectionUrl);
    this.lastScrollY = window.scrollY;
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("pagehide", this.onPageHide);
    window.addEventListener("pageshow", this.onPageShow);
    window.addEventListener("wheel", this.onRestoreIntent, { passive: true });
    window.addEventListener("touchmove", this.onRestoreIntent, { passive: true });
    window.addEventListener("keydown", this.onRestoreIntent);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    document.addEventListener("prerenderingchange", this.onPageShow);
    this.productList?.addEventListener("click", this.onProductLinkClick);
    document.addEventListener("facet:update", this.onFacetUpdate, { capture: true });
    document.addEventListener("facet:update:complete", this.onFacetUpdateComplete);
    // BEGIN CUSTOM EDIT: Collection section navigation lifecycle | Date: 2026-09-16 | What: Coordinate loader state with collection-level Section Rendering
    document.addEventListener("collection:navigation:start", this.onCollectionNavigationStart, { capture: true });
    document.addEventListener("collection:navigation:complete", this.onCollectionNavigationComplete);
    // END CUSTOM EDIT
    this.setAttribute("data-enhanced", "");
    this.restoration = this.restoreCollection(this.readHistoryState());
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("pagehide", this.onPageHide);
    window.removeEventListener("pageshow", this.onPageShow);
    window.removeEventListener("wheel", this.onRestoreIntent);
    window.removeEventListener("touchmove", this.onRestoreIntent);
    window.removeEventListener("keydown", this.onRestoreIntent);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    document.removeEventListener("prerenderingchange", this.onPageShow);
    clearTimeout(this.historySaveTimer);
    this.cancelRestoration();
    this.abortCurrentRequest();
    this.observer?.disconnect();
    this.productList?.removeEventListener("click", this.onProductLinkClick);
    document.removeEventListener("facet:update", this.onFacetUpdate, { capture: true });
    document.removeEventListener("facet:update:complete", this.onFacetUpdateComplete);
    // BEGIN CUSTOM EDIT: Collection section navigation lifecycle | Date: 2026-09-16 | What: Remove delegated navigation lifecycle listeners
    document.removeEventListener("collection:navigation:start", this.onCollectionNavigationStart, { capture: true });
    document.removeEventListener("collection:navigation:complete", this.onCollectionNavigationComplete);
    // END CUSTOM EDIT
  }

  get nextLink() {
    return this.pagination?.querySelector('a[rel="next"]');
  }

  get loadMoreButton() {
    return this.pagination?.querySelector("[data-load-more]");
  }

  get historyEnabled() {
    return !window.Shopify?.designMode && !document.prerendering;
  }

  refreshControls() {
    this.loadMoreButton?.removeEventListener("click", this.onLoadMoreClick);
    this.loadMoreButton?.addEventListener("click", this.onLoadMoreClick);
    if (this.loadMoreButton) {
      this.loadMoreButton.hidden = this.isRestoring || (!this.nextLink && !this.pendingRestore);
    }
    this.setButtonText(this.hasError ? this.dataset.retryText : this.dataset.loadMoreText);
    this.observePagination();
    this.updateCompletedState();
  }

  observePagination() {
    this.observer?.disconnect();

    if (!this.pagination || !this.nextLink || this.hasError || this.isReplacing || this.isRestoring || !("IntersectionObserver" in window)) {
      return;
    }

    this.observer = new IntersectionObserver(this.onIntersection, { rootMargin: "0px 0px 2000px 0px" });
    this.observer.observe(this.pagination);
  }

  onScroll() {
    const scrollY = window.scrollY;
    const movedDown = scrollY > this.lastScrollY;
    if (scrollY < this.lastScrollY) this.autoLoadArmed = false;
    this.lastScrollY = scrollY;
    if (this.navigationAnchor && Math.abs(scrollY - this.navigationScrollY) > 1) this.navigationAnchor = null;
    if (!this.isRestoring && !this.isReplacing) {
      clearTimeout(this.historySaveTimer);
      this.historySaveTimer = setTimeout(() => this.saveHistory(), 500);
    }

    if (!movedDown || this.autoLoadArmed || !this.canAutoLoad()) return;

    this.autoLoadArmed = true;
    // Reobserve once per downward scroll phase, even if already inside the margin.
    this.observePagination();
  }

  onIntersection(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) {
      return;
    }

    if (!this.autoLoadArmed || !this.canAutoLoad()) {
      this.autoLoadArmed = false;
      return;
    }

    this.loadNextPage({ manual: false });
  }

  canAutoLoad() {
    const activeElement = document.activeElement;

    if (activeElement && activeElement !== document.body && !this.contains(activeElement) && activeElement.matches(":focus-visible")) {
      return false;
    }

    return !this.isLoading && !this.hasError && !this.isReplacing && !this.isRestoring && Boolean(this.nextLink);
  }

  onLoadMoreClick(event) {
    event.preventDefault();
    if (this.pendingRestore) this.restoration = this.restoreCollection(this.pendingRestore);
    else this.loadNextPage({ manual: true });
  }

  onFacetUpdate(event) {
    const sectionId = event.detail?.url?.searchParams?.get?.("section_id");

    // BEGIN CUSTOM EDIT: Targeted facet suspension | Date: 2026-09-15 | What: Ignore malformed or untargeted facet events so loading does not freeze
    if (sectionId !== this.sectionId) {
      return;
    }
    // END CUSTOM EDIT

    this.isReplacing = true;
    this.cancelRestoration();
    clearTimeout(this.historySaveTimer);
    this.abortCurrentRequest();
    this.observer?.disconnect();
  }

  onFacetUpdateComplete(event) {
    const { sectionId, success } = event.detail || {};

    if (sectionId !== this.sectionId || success) {
      return;
    }

    this.isReplacing = false;
    if (this.pendingRestore) this.restoration = this.restoreCollection(this.pendingRestore);
    else this.refreshControls();
  }

  // BEGIN CUSTOM EDIT: Collection section navigation lifecycle | Date: 2026-09-16 | What: Let the collection router replace sections without stale loader work
  onCollectionNavigationStart(event) {
    const { sectionId, historyNavigation } = event.detail || {};

    if (sectionId !== this.sectionId) {
      return;
    }

    if (!historyNavigation) {
      this.saveHistory();
    }

    this.isReplacing = true;
    this.cancelRestoration();
    clearTimeout(this.historySaveTimer);
    this.abortCurrentRequest();
    this.observer?.disconnect();
  }

  onCollectionNavigationComplete(event) {
    const { sectionId, success } = event.detail || {};

    if (sectionId !== this.sectionId || success) {
      return;
    }

    this.isReplacing = false;
    if (this.pendingRestore) this.restoration = this.restoreCollection(this.pendingRestore);
    else this.refreshControls();
  }
  // END CUSTOM EDIT

  async loadNextPage({ manual = false, restoring = false } = {}) {
    const nextLink = this.nextLink;

    if (!nextLink || this.isLoading || this.isReplacing || (this.isRestoring && !restoring)) {
      return false;
    }

    const requestedUrl = new URL(nextLink.href, window.location.href);
    const requestedPage = Number(requestedUrl.searchParams.get("page") || 1);

    if (!Number.isInteger(requestedPage) || requestedPage !== this.currentPage + 1 || requestedPage > this.pageCount) {
      return false;
    }

    if (requestedUrl.origin !== window.location.origin || requestedUrl.pathname !== window.location.pathname) {
      return false;
    }

    this.preserveCurrentContextParams(requestedUrl);
    const expectedContext = this.canonicalRequestContext(requestedUrl);

    if (expectedContext !== this.canonicalRequestContext(new URL(window.location.href))) {
      return false;
    }

    requestedUrl.searchParams.set("section_id", this.sectionId);
    requestedUrl.searchParams.delete("sections");

    this.isLoading = true;
    this.autoLoadArmed = false;
    this.hasError = false;
    this.setBusy(true);
    this.setStatus("");
    this.setButtonText(this.dataset.loadingText);
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    try {
      const { parsedDocument, incomingList, incomingPagination } = await this.fetchPage(requestedUrl, requestedPage, signal);
      if (!this.canCommitRequest(signal)) return false;
      this.preserveSectionStyles(parsedDocument);
      const appendedItems = this.appendNewItems(incomingList, requestedPage);

      if (appendedItems.length === 0) {
        throw new Error("Collection response did not contain new items");
      }

      const incomingNavigation = incomingPagination.querySelector(".pagination");
      const currentNavigation = this.pagination.querySelector(".pagination");
      if (incomingNavigation) {
        currentNavigation?.replaceWith(document.importNode(incomingNavigation, true));
      } else {
        currentNavigation?.remove();
      }
      this.currentPage = requestedPage;
      this.dataset.page = String(requestedPage);
      this.hasLoadedPage = true;
      this.setStatus(this.nextLink ? this.dataset.loadedText : this.dataset.endText);

      if (manual) {
        this.focusFirstNewItem(appendedItems[0]);
      }

      this.refreshControls();
      this.saveHistory();
      return true;
    } catch (error) {
      if (!signal.aborted) {
        this.hasError = true;
        this.setStatus(this.dataset.errorText);
        this.setButtonText(this.dataset.retryText);
        this.observer?.disconnect();
      }
      return false;
    } finally {
      if (!signal.aborted) {
        this.isLoading = false;
        this.setBusy(false);
      }
      if (this.abortController?.signal === signal) this.abortController = null;
    }
  }

  canCommitRequest(signal) {
    return !signal.aborted && this.isConnected && !this.isReplacing && this.contextKey(new URL(location.href)) === this.requestContext;
  }

  async fetchPage(url, page, signal) {
    const response = await fetch(url.toString(), { credentials: "same-origin", signal });
    const responseUrl = new URL(response.url);
    if (!response.ok) throw new Error(`Section request failed with ${response.status}`);
    if (responseUrl.origin !== location.origin || responseUrl.pathname !== url.pathname) throw new Error("Collection response changed path");
    if (this.contextKey(responseUrl) !== this.requestContext) throw new Error("Collection response changed query context");
    const parsedDocument = new DOMParser().parseFromString(await response.text(), "text/html");
    const incomingLoader = this.findIncomingLoader(parsedDocument);
    const incomingList = incomingLoader?.querySelector("[data-collection-product-list]");
    const incomingPagination = incomingLoader?.querySelector("[data-collection-pagination]");
    if (!incomingList || !incomingPagination || Number(incomingLoader.dataset.page) !== page) throw new Error("Malformed or non-advancing collection response");
    return { parsedDocument, incomingList, incomingPagination };
  }

  findIncomingLoader(parsedDocument) {
    return Array.from(parsedDocument.querySelectorAll("collection-infinite-scroll")).find((element) => {
      return element.getAttribute("section-id") === this.sectionId;
    });
  }

  preserveCurrentContextParams(url) {
    const currentParams = new URLSearchParams(window.location.search);

    currentParams.forEach((value, key) => {
      if (!this.isPaginationParam(key) && !url.searchParams.has(key)) {
        url.searchParams.append(key, value);
      }
    });
  }

  appendNewItems(incomingList, page) {
    const appendedItems = [];
    const fragment = document.createDocumentFragment();
    const newKeys = [];

    Array.from(incomingList.children).forEach((item) => {
      const key = this.itemKey(item);

      if (!key || !this.isAllowedGridItem(item)) {
        throw new Error("Collection response contained an unknown grid item");
      }

      if (key && this.loadedKeys.has(key)) {
        return;
      }

      const importedItem = document.importNode(item, true);
      importedItem.dataset.page = importedItem.dataset.page || String(page);
      this.prepareAppendedImages(importedItem);
      fragment.append(importedItem);
      newKeys.push(key);
      appendedItems.push(importedItem);
    });

    if (appendedItems.length > 0) {
      this.productList.append(fragment);
      newKeys.forEach((key) => this.loadedKeys.add(key));
      document.dispatchEvent(new CustomEvent("collection:analytics:items-added", {
        detail: {
          root: this.productList,
          items: appendedItems,
          page
        }
      }));
    }

    return appendedItems;
  }

  isAllowedGridItem(item) {
    return item.matches("product-card") || item.classList.contains("product-list__promo");
  }

  itemKey(item) {
    if (item.dataset.productId) {
      return `product:${item.dataset.productId}`;
    }

    if (item.dataset.promoId) {
      return `promo:${item.dataset.promoId}`;
    }

    if (item.matches("product-card") && item.getAttribute("handle")) {
      return `product:${item.getAttribute("handle")}`;
    }

    if (item.id) {
      return `id:${item.id}`;
    }

    return "";
  }

  prepareAppendedImages(item) {
    item.querySelectorAll("img").forEach((image) => {
      image.loading = "lazy";
      image.removeAttribute("fetchpriority");

      if (image.classList.contains("product-card__image--secondary")) {
        image.fetchPriority = "low";
      }
    });
  }

  canonicalRequestContext(url) {
    const pairs = [];

    url.searchParams.forEach((value, key) => {
      if (!this.isPaginationParam(key)) {
        pairs.push([key, value]);
      }
    });

    pairs.sort(([keyA, valueA], [keyB, valueB]) => {
      if (keyA === keyB) {
        return valueA.localeCompare(valueB);
      }

      return keyA.localeCompare(keyB);
    });

    return `${url.pathname}?${pairs.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join("&")}`;
  }

  isPaginationParam(key) {
    return key === "page" || key === "section_id" || key === "sections" || key === "sections[]";
  }

  preserveSectionStyles(parsedDocument) {
    if (!this.section) {
      return;
    }

    const currentStyles = new Set(Array.from(this.section.querySelectorAll("style[data-section-stylesheet]")).map((style) => style.textContent));

    parsedDocument.querySelectorAll("style[data-section-stylesheet]").forEach((style) => {
      if (currentStyles.has(style.textContent)) {
        return;
      }

      this.section.prepend(document.importNode(style, true));
      currentStyles.add(style.textContent);
    });
  }

  cleanUrl(url) {
    const clean = new URL(url.toString());
    for (const key of [...clean.searchParams.keys()]) {
      if (this.isPaginationParam(key)) clean.searchParams.delete(key);
    }
    return clean;
  }

  contextKey(url) {
    return this.canonicalRequestContext(url);
  }

  readHistoryState() {
    if (!this.historyEnabled) return null;
    const saved = history.state?.collectionInfiniteScroll;
    if (!saved || typeof saved.context !== "string") return null;
    try {
      const url = new URL(saved.context, location.origin);
      if (url.origin !== location.origin || this.contextKey(url) !== this.requestContext) return null;
    } catch { return null; }
    const loadedPage = Number(saved.loadedPage ?? saved.page);
    if (!Number.isInteger(loadedPage) || loadedPage < 1) return null;
    return {
      loadedPage: Math.min(loadedPage, this.pageCount),
      anchor: typeof saved.anchor === "string" ? saved.anchor : "",
      offset: Number.isFinite(saved.offset) ? saved.offset : 0,
      scrollY: Number.isFinite(saved.scrollY) ? Math.max(0, saved.scrollY) : 0
    };
  }

  visibleItem() {
    return Array.from(this.productList?.children || []).find(item => {
      const rect = item.getBoundingClientRect();
      return rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    });
  }

  saveHistory(item = this.navigationAnchor || this.visibleItem()) {
    if (!this.historyEnabled || this.isRestoring || this.isReplacing || this.firstLoadedPage > 1 || !this.isConnected) return;
    if (this.contextKey(new URL(location.href)) !== this.requestContext) return;
    if (this.pendingRestore && !this.navigationAnchor && !this.restoreScrollCancelled) return;
    const state = {
      version: 2,
      loadedPage: Math.max(this.currentPage, this.pendingRestore?.loadedPage || 1),
      page: Number(item?.dataset.page || 1),
      anchor: item ? this.itemKey(item) : "",
      offset: item ? item.getBoundingClientRect().top : 0,
      scrollY: window.scrollY,
      context: this.requestContext
    };
    if (this.pendingRestore) this.pendingRestore = { ...state };
    try {
      history.replaceState({ ...(history.state || {}), collectionInfiniteScroll: state }, "", this.collectionUrl.toString());
    } catch (error) {
      // History may be restricted in embedded/private contexts; browsing must remain usable.
      console.warn("Collection position could not be saved", error);
    }
  }

  onProductLinkClick(event) {
    const link = event.target.closest("a[href]");
    const item = event.target.closest("[data-product-id], product-card");
    if (event.defaultPrevented || !link || !item) return;
    this.navigationAnchor = item;
    this.navigationScrollY = window.scrollY;
    clearTimeout(this.historySaveTimer);
    this.saveHistory(item);
  }

  onPageHide() {
    clearTimeout(this.historySaveTimer);
    this.saveHistory();
    this.cancelRestoration();
    this.abortCurrentRequest();
  }

  onPageShow(event) {
    if (event.persisted || event.type === "prerenderingchange") this.restoration = this.restoreCollection(this.readHistoryState());
  }

  onVisibilityChange() {
    if (document.visibilityState === "hidden") {
      clearTimeout(this.historySaveTimer);
      this.saveHistory();
    }
  }

  onRestoreIntent(event) {
    if (this.pendingRestore && !event.ctrlKey && !event.metaKey && !event.altKey) this.restoreScrollCancelled = true;
  }

  takeScrollRestoration() {
    if (!this.historyEnabled || this.previousScrollRestoration !== undefined) return;
    this.previousScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = "manual";
  }

  releaseScrollRestoration() {
    if (this.previousScrollRestoration === undefined) return;
    history.scrollRestoration = this.previousScrollRestoration;
    this.previousScrollRestoration = undefined;
  }

  cancelRestoration() {
    this.restorationId++;
    this.isRestoring = false;
    this.removeAttribute("data-restoring");
    this.releaseScrollRestoration();
  }

  async restoreCollection(saved) {
    if (this.isRestoring || this.isReplacing || !this.isConnected) return;
    const targetPage = Math.max(this.currentPage, Math.min(saved?.loadedPage || this.currentPage, this.pageCount));
    const needsRestore = this.firstLoadedPage > 1 || targetPage > this.currentPage || saved?.anchor || saved?.scrollY;
    if (!needsRestore) {
      this.pendingRestore = null;
      this.refreshControls();
      this.saveHistory();
      return;
    }

    const id = ++this.restorationId;
    this.pendingRestore = { ...saved, loadedPage: targetPage };
    this.isRestoring = true;
    this.restoreScrollCancelled = false;
    this.setAttribute("data-enhanced", "");
    this.setAttribute("data-restoring", "");
    if (this.statusElement) this.statusElement.textContent = "";
    this.takeScrollRestoration();
    this.refreshControls();
    try {
      if (this.firstLoadedPage > 1) await this.restoreEarlierPages();
      if (id !== this.restorationId || !this.isConnected) return;
      while (this.currentPage < targetPage) {
        const loaded = await this.loadNextPage({ manual: false, restoring: true });
        if (id !== this.restorationId || !this.isConnected) return;
        if (!loaded) throw new Error("Collection restoration could not load the next batch");
      }
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (id !== this.restorationId || !this.isConnected) return;
      if (saved && !this.restoreScrollCancelled) this.restorePosition(saved);
      this.pendingRestore = null;
      this.hasError = false;
      this.hasLoadedPage = false; // Restoring existing content is not a new loading announcement.
    } catch (error) {
      if (id !== this.restorationId || !this.isConnected) return;
      this.hasError = true;
      // An incomplete legacy entry retains its real URL and native navigation on failure.
      if (this.firstLoadedPage > 1) this.removeAttribute("data-enhanced");
    } finally {
      if (id === this.restorationId) {
        this.isRestoring = false;
        this.removeAttribute("data-restoring");
        this.setBusy(false);
        this.refreshControls();
        if (this.hasError) this.setStatus(this.dataset.errorText);
        else this.saveHistory();
      }
    }
  }

  async restoreEarlierPages() {
    const controller = new AbortController();
    this.abortController = controller;
    this.isLoading = true;
    this.setBusy(true);
    const pages = [];
    const fragment = document.createDocumentFragment();
    const keys = new Set(this.loadedKeys);
    const items = [];
    try {
      for (let page = 1; page < this.firstLoadedPage; page++) {
        const url = new URL(this.collectionUrl.toString());
        url.searchParams.set("page", String(page));
        url.searchParams.set("section_id", this.sectionId);
        const incoming = await this.fetchPage(url, page, controller.signal);
        if (!this.canCommitRequest(controller.signal)) throw new DOMException("Restoration cancelled", "AbortError");
        const batch = [];
        for (const item of incoming.incomingList.children) {
          const key = this.itemKey(item);
          if (!key || !this.isAllowedGridItem(item)) throw new Error("Collection response contained an unknown grid item");
          if (keys.has(key)) continue;
          const imported = document.importNode(item, true);
          imported.dataset.page = String(page);
          this.prepareAppendedImages(imported);
          fragment.append(imported);
          keys.add(key);
          batch.push(imported);
        }
        if (!batch.length) throw new Error("Earlier collection page contained no new products");
        pages.push(incoming.parsedDocument);
        items.push(...batch);
      }
      if (!this.canCommitRequest(controller.signal)) throw new DOMException("Restoration cancelled", "AbortError");
      const visible = this.visibleItem();
      const before = visible?.getBoundingClientRect().top;
      this.productList.prepend(fragment);
      this.loadedKeys = keys;
      this.firstLoadedPage = 1;
      pages.forEach(doc => this.preserveSectionStyles(doc));
      // Preserve what the shopper is reading if they scroll while earlier batches arrive.
      if (visible) window.scrollBy({ top: visible.getBoundingClientRect().top - before });
      this.lastScrollY = window.scrollY;
      document.dispatchEvent(new CustomEvent("collection:analytics:items-added", {
        detail: { root: this.productList, items }
      }));
    } finally {
      if (this.abortController === controller) {
        this.abortController = null;
        this.isLoading = false;
        this.setBusy(false);
      }
    }
  }

  restorePosition(saved) {
    const item = Array.from(this.productList?.children || []).find(child => this.itemKey(child) === saved.anchor);
    if (item) {
      const rect = item.getBoundingClientRect();
      const offset = Math.max(24 - rect.height, Math.min(saved.offset || 0, window.innerHeight - 24));
      window.scrollTo({ top: window.scrollY + rect.top - offset, behavior: "instant" });
      this.navigationAnchor = item;
      this.navigationScrollY = window.scrollY;
    } else if (Number.isFinite(saved.scrollY)) {
      window.scrollTo({ top: saved.scrollY, behavior: "instant" });
    }
    this.lastScrollY = window.scrollY;
  }

  setExistingItemPages(page) {
    Array.from(this.productList?.children || []).forEach((item) => {
      item.dataset.page = item.dataset.page || String(page);
    });
  }

  focusFirstNewItem(item) {
    const focusTarget = item.matches("a[href]") ? item : item.querySelector("a[href]");

    if (!focusTarget) {
      return;
    }

    const hadTabindex = focusTarget.hasAttribute("tabindex");
    const previousTabindex = focusTarget.getAttribute("tabindex");
    focusTarget.setAttribute("tabindex", "-1");
    focusTarget.addEventListener("blur", () => {
      if (hadTabindex) {
        focusTarget.setAttribute("tabindex", previousTabindex);
      } else {
        focusTarget.removeAttribute("tabindex");
      }
    }, { once: true });
    focusTarget.focus({ preventScroll: true });
  }

  updateCompletedState() {
    if (this.nextLink) {
      this.removeAttribute("data-complete");
      return;
    }

    this.setAttribute("data-complete", "");
    // BEGIN CUSTOM EDIT: Collection completion announcements | Date: 2026-09-15 | What: Announce completion only after this loader appended the final page
    if (this.hasLoadedPage && this.firstLoadedPage === 1) {
      this.setStatus(this.dataset.endText);
    }
    // END CUSTOM EDIT
  }

  setBusy(isBusy) {
    isBusy = isBusy || this.isRestoring;
    this.setAttribute("aria-busy", isBusy ? "true" : "false");

    if (this.loadMoreButton) {
      this.loadMoreButton.disabled = isBusy;
    }
  }

  setStatus(text) {
    if (this.statusElement && !this.isRestoring) {
      this.statusElement.textContent = text || "";
    }
  }

  setButtonText(text) {
    if (this.loadMoreButton && text) {
      this.loadMoreButton.textContent = text;
    }
  }

  abortCurrentRequest() {
    this.autoLoadArmed = false;
    this.abortController?.abort();
    this.abortController = null;
    this.isLoading = false;
    this.setBusy(false);
  }
}

if (!window.customElements.get("collection-infinite-scroll")) {
  window.customElements.define("collection-infinite-scroll", CollectionInfiniteScroll);
}

// END CUSTOM EDIT
